/**
 * Environment Validation Utility
 * Validates required environment variables at startup
 * Logs warnings for missing optional vars
 *
 * Every value is read as a literal `import.meta.env.VITE_X`. Reading one by a
 * computed name (`import.meta.env[name]`) makes Vite put EVERY VITE_ variable
 * of the build into the public bundle, because it cannot tell which are used.
 * That is how VITE_CLAUDE_PROXY_KEY and the deployment's VITE_VERCEL_* values
 * reached every visitor until Oct 2026. scripts/check-bundle-secrets.mjs fails
 * the build if a secret-looking variable ever ships again.
 */

import * as Sentry from '@sentry/react'

interface EnvConfig {
  name: string
  value: string | undefined
  required: boolean
  description: string
}

const ENV_VARS: EnvConfig[] = [
  { name: 'VITE_POCKETBASE_URL', value: import.meta.env.VITE_POCKETBASE_URL, required: true, description: 'PocketBase server URL' },
  { name: 'VITE_SENTRY_DSN', value: import.meta.env.VITE_SENTRY_DSN, required: false, description: 'Sentry error tracking DSN' },
  { name: 'VITE_SCRAPER_API_URL', value: import.meta.env.VITE_SCRAPER_API_URL, required: false, description: 'Player data scraper API URL' },
  { name: 'VITE_SUPPORT_EMAIL', value: import.meta.env.VITE_SUPPORT_EMAIL, required: false, description: 'Support email address' },
]

export interface ValidationResult {
  valid: boolean
  missing: string[]
  warnings: string[]
}

/**
 * Validates environment variables at application startup
 * Returns validation result with missing required vars and warnings
 */
export function validateEnv(): ValidationResult {
  const missing: string[] = []
  const warnings: string[] = []

  for (const config of ENV_VARS) {
    const value = config.value
    if (!value || value === 'undefined' || value === '') {
      if (config.required) {
        missing.push(`${config.name} (${config.description})`)
      } else {
        warnings.push(`${config.name} not set - ${config.description}`)
      }
    }
  }

  // Log warnings in development
  if (import.meta.env.DEV && warnings.length > 0) {
    console.warn('Optional environment variables not set:')
    warnings.forEach((w) => console.warn(`  - ${w}`))
  }

  // Report missing required vars to Sentry
  if (missing.length > 0) {
    const error = new Error(`Missing required environment variables: ${missing.join(', ')}`)
    if (import.meta.env.VITE_SENTRY_DSN) {
      Sentry.captureException(error)
    }
  }

  return {
    valid: missing.length === 0,
    missing,
    warnings,
  }
}

/**
 * Checks if the app is properly configured for production
 */
export function isProductionReady(): boolean {
  const result = validateEnv()
  return result.valid && import.meta.env.PROD
}

export default validateEnv
